import * as z from 'zod';

import { FeeId, PaymentBreakdownId, PaymentDueId, PaymentId, PayoffId } from '../BrandedIds';
import { zodBrandedUuid, zodDateOrString } from '../utils/Zod';
import { Payoff } from './Payoffs';

export const StatementPaymentBreakdownType = z.literal('statement');
export const SuspensePaymentBreakdownType = z.literal('suspense');
export const PrincipalPrepaymentPaymentBreakdownType = z.literal('principalPrepayment');
export const FeePaymentBreakdownType = z.literal('fee');
export const AdvancePaymentBreakdownType = z.literal('advance');
export const PayoffPaymentBreakdownType = z.literal('payoff');
export const ThirdPartyDepositBreakdownType = z.literal('thirdParty');
export const BalanceReallocationBreakdownType = z.literal('balanceReallocation');

export const PaymentBucket = z.enum([
  'principal',
  'interest',
  'escrow',
  'reserve',
  'fees',
  'advance',
  'additionalPrincipal',
  'additionalEscrow',
  'holdback',
  'suspense',
  'futureStatementSuspense',
  'unappliedSuspense',
]);
export type PaymentBucket = z.infer<typeof PaymentBucket>;

export const PaymentBreakdownType = z.union([
  StatementPaymentBreakdownType,
  SuspensePaymentBreakdownType,
  PrincipalPrepaymentPaymentBreakdownType,
  FeePaymentBreakdownType,
  AdvancePaymentBreakdownType,
  PayoffPaymentBreakdownType,
  ThirdPartyDepositBreakdownType,
  BalanceReallocationBreakdownType,
]);
export type PaymentBreakdownType = z.infer<typeof PaymentBreakdownType>;

export type PaymentBreakdownEvent = z.infer<typeof PaymentBreakdownEvent>;
export const PaymentBreakdownEvent = z.enum(['initiated', 'failedOrCancelled', 'reallocated', 'voided']);

const BaseBreakdown = z.object({
  id: zodBrandedUuid<PaymentBreakdownId>().optional(), // TODO migrate & require
  createdAt: zodDateOrString.default(new Date('1970-01-01T00:00:00.000Z')), // TODO migrate & no default

  /**
   * The event that caused this breakdown to be created.
   * This is used to track the history of the breakdown and to determine if it should be considered for calculations.
   *
   * If it is 'initiated', it means the breakdown was created as part of a payment being initiated.
   * If it is 'failedOrCancelled', it means the breakdown was created as part of a payment being failed or cancelled.
   * If it is 'reallocated', it means the breakdown was created as part of a payment being reallocated.
   */
  event: z.enum(['initiated', 'failedOrCancelled', 'reallocated', 'voided']).default('initiated'), // TODO migrate & no default

  paymentId: zodBrandedUuid<PaymentId>(),
  amount: z.number(),
  expectedPayment: z.number(),
});

const StatementBreakdown = BaseBreakdown.extend({
  type: StatementPaymentBreakdownType,
  paymentDueId: zodBrandedUuid<PaymentDueId>(),
  principal: z.number(),
  interest: z.number(),
  escrow: z.number(),
  additionalPrincipal: z.number(),
  additionalEscrow: z.number(),
  suspense: z.number(), // statement suspense
  futureStatementSuspense: z.number().default(0),
  unappliedSuspense: z.number().default(0),
  reserve: z.number().optional(),
});
export type StatementBreakdown = z.infer<typeof StatementBreakdown>;

const SuspenseBreakdown = BaseBreakdown.extend({
  type: SuspensePaymentBreakdownType,
  futureStatementSuspense: z.number(),
  unappliedSuspense: z.number().default(0),
});
export type SuspenseBreakdown = z.infer<typeof SuspenseBreakdown>;

const PrincipalPrepaymentBreakdown = BaseBreakdown.extend({
  type: PrincipalPrepaymentPaymentBreakdownType,
  additionalPrincipal: z.number(),
  reserve: z.number().optional(),
});
export type PrincipalPrepaymentBreakdown = z.infer<typeof PrincipalPrepaymentBreakdown>;

const FeeBreakdown = BaseBreakdown.extend({
  type: FeePaymentBreakdownType,
  feeId: zodBrandedUuid<FeeId>(),
  fees: z.number(),
  unappliedSuspense: z.number().default(0),
});
export type FeeBreakdown = z.infer<typeof FeeBreakdown>;

const AdvanceBreakdown = BaseBreakdown.extend({
  type: AdvancePaymentBreakdownType,
  feeId: zodBrandedUuid<FeeId>(),
  advance: z.number(),
  advanceInterest: z.number(),
  unappliedSuspense: z.number().default(0),
});
export type AdvanceBreakdown = z.infer<typeof AdvanceBreakdown>;

const PayoffBreakdown = BaseBreakdown.extend({
  type: PayoffPaymentBreakdownType,
  payoffId: zodBrandedUuid<PayoffId>(),
  principal: z.number(),
  interest: z.number(),
  escrow: z.number(),
  suspense: z.number(), // statement suspense
  futureStatementSuspense: z.number().default(0),
  unappliedSuspense: z.number().default(0),
  reserve: z.number(),
  fees: z.number(),
  additionalPrincipal: z.number().optional(),
  additionalEscrow: z.number().optional(),
});
export type PayoffBreakdown = z.infer<typeof PayoffBreakdown>;

const ThirdPartyBreakdown = BaseBreakdown.extend({
  type: ThirdPartyDepositBreakdownType,
  escrow: z.number(),
  holdback: z.number(),
  suspense: z.number().optional(),
  unappliedSuspense: z.number().default(0),
  principal: z.number(),
});
export type ThirdPartyBreakdown = z.infer<typeof ThirdPartyBreakdown>;

const BalanceReallocationBreakdown = BaseBreakdown.extend({
  type: BalanceReallocationBreakdownType,
  escrow: z.number(),
  holdback: z.number(),
  suspense: z.number().optional(),
  futureStatementSuspense: z.number().default(0),
  unappliedSuspense: z.number().default(0),
  principal: z.number(),
});
export type BalanceReallocationBreakdown = z.infer<typeof BalanceReallocationBreakdown>;

export const PaymentBreakdown = z.discriminatedUnion('type', [
  StatementBreakdown,
  SuspenseBreakdown,
  PrincipalPrepaymentBreakdown,
  FeeBreakdown,
  AdvanceBreakdown,
  PayoffBreakdown,
  ThirdPartyBreakdown,
  BalanceReallocationBreakdown,
]);
export type PaymentBreakdown = z.infer<typeof PaymentBreakdown>;

export const ChargeBreakdown = z.discriminatedUnion('type', [FeeBreakdown, AdvanceBreakdown]);
export type ChargeBreakdown = z.infer<typeof ChargeBreakdown>;

export function isStatementBreakdown(item: PaymentBreakdown): item is StatementBreakdown {
  return item.type === 'statement';
}
export function isSuspenseBreakdown(item: PaymentBreakdown): item is SuspenseBreakdown {
  return item.type === 'suspense';
}
export function isPrincipalPrepaymentBreakdown(item: PaymentBreakdown): item is PrincipalPrepaymentBreakdown {
  return item.type === 'principalPrepayment';
}
export function isFeeBreakdown(item: PaymentBreakdown): item is FeeBreakdown {
  return item.type === 'fee';
}
export function isThirdPartyBreakdown(item: PaymentBreakdown): item is ThirdPartyBreakdown {
  return item.type === 'thirdParty';
}
export function isBalanceReallocationBreakdown(item: PaymentBreakdown): item is BalanceReallocationBreakdown {
  return item.type === 'balanceReallocation';
}

export function isAdvanceBreakdown(item: PaymentBreakdown): item is AdvanceBreakdown {
  return item.type === 'advance';
}

export function isChargeBreakdown(item: PaymentBreakdown): item is ChargeBreakdown {
  return item.type === 'fee' || item.type === 'advance';
}

export function isPayoffBreakdown(item: PaymentBreakdown): item is PayoffBreakdown {
  return item.type === 'payoff';
}

export function isFullPayoffBreakdown(item: PaymentBreakdown, payoffs: Payoff[]): item is PayoffBreakdown {
  const fullPayoffIds = payoffs.filter((p) => p.isPartial !== true).map((p) => p.id);
  return item.type === 'payoff' && fullPayoffIds.includes(item.payoffId);
}

export function isPartialPayoffBreakdown(item: PaymentBreakdown, payoffs: Payoff[]): item is PayoffBreakdown {
  const partialPayoffIds = payoffs.filter((p) => p.isPartial === true).map((p) => p.id);
  return item.type === 'payoff' && partialPayoffIds.includes(item.payoffId);
}

export const extractValueFromBreakdown = (breakdown: PaymentBreakdown, bucket: PaymentBucket): number =>
  bucket in breakdown ? (breakdown as any)[bucket] : 0;
